import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../../../../pdc-platform/src/ts/Glob';
import NumberField from '../NumberField';
import SelectField from '../SelectField';

import { WidthUnitsType, IWidth, IWidthObject } from '../../ts/Interfaces';
import ParameterFieldBase, { IParameterFieldBaseOptions } from '../../ts/ParameterFieldBase';

function isWidthEmpty(width: number | string) {
    return width == null || width === '' || (typeof width === 'number' ? width : parseInt(width, 10)) <= 0;
}

export function normalizeWidth(value: IWidthObject) {
    if (value == null) {
        return;
    }

    if (value.units === 'auto') {
        delete value.width;
    } else if (value.units === 'default' || isWidthEmpty(value.width)) {
        delete value.width;
        delete value.units;
    } else {
        if (typeof value.width !== 'number') {
            value.width = parseInt(value.width, 10);

            if (Number.isNaN(value.width)) {
                delete value.width;
                delete value.units;
            }
        }

        if (value.units == null || value.units === 'default') {
            value.units = 'pixels';
        }
    }
}

export interface IWidthSelectorOptions extends IParameterFieldBaseOptions {
    hideOptionAuto?: boolean;
}

export interface IWidthSelectorProperties extends IWidthSelectorOptions {
    value?: IWidth;
    allowNull?: boolean;
    options?: IWidthSelectorOptions;

    onChange?(value: IWidthObject): void;
}

export default function WidthSelector(props: IWidthSelectorProperties) {
    let width: IWidthObject;

    if (props.value != null) {
        if (typeof props.value === 'number') {
            width = { width: props.value, units: 'pixels' };
        } else {
            width = { ...props.value };
        }
    } else {
        width = {};
    }

    const processValueChange = (newWidth: IWidthObject) => {
        normalizeWidth(newWidth);

        if ((props.allowNull || props.options?.allowNull) && _.isEmpty(newWidth)) {
            newWidth = null;
        }

        if (typeof props.onChange === 'function') {
            props.onChange(newWidth);
        }
    };

    const handleOnWidthChange = (value: number) => {
        const newWidth = { ...width };

        newWidth.width = value;

        if (!isWidthEmpty(newWidth.width) && (newWidth.units === 'auto' || newWidth.units === 'default')) {
            delete newWidth.units;
        }

        processValueChange(newWidth);
    };

    const handleOnUnitsChange = (value: string) => {
        const newWidth = { ...width };

        newWidth.units = value as WidthUnitsType;

        if (isWidthEmpty(newWidth.width) && (newWidth.units === 'percentage' || newWidth.units === 'pixels')) {
            newWidth.width = 20;
        }

        processValueChange(newWidth);
    };

    let optionAuto: React.JSX.Element;

    if (!props.hideOptionAuto && !props.options?.hideOptionAuto) {
        optionAuto = <option value={'auto'}>{'auto'}</option>;
    }

    return (
        <div className="width-selector">
            <table className="table">
                <tbody>
                    <tr>
                        <td className="width-field">
                            <NumberField
                                value={width.width}
                                integer={true}
                                allowNull={true}
                                applyOnBlur={true}
                                onChange={handleOnWidthChange}
                            />
                        </td>

                        <td className="units-field">
                            <SelectField value={width.units || 'default'} onChange={handleOnUnitsChange}>
                                <option value={'default'}>{'not set'}</option>
                                {optionAuto}
                                <option value={'pixels'}>{'pixels'}</option>
                                <option value={'percentage'}>{'%'}</option>
                            </SelectField>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

export class WidthSelectorField extends ParameterFieldBase<IWidthSelectorOptions> {
    public static EditComponent = WidthSelector;

    public getDefaultOptions() {
        return {
            allowNull: true
        };
    }

    constructor(
        glob: IGlob,
        title: string,
        options?: IWidthSelectorOptions,
        directionOptions?: IWidthSelectorProperties
    ) {
        super(glob, title, [], [], null, options, directionOptions);
    }
}
